import { RefObject, useCallback, useEffect, useRef } from "react";
import { ViewTableHandle } from "@design-system/ViewTable/ViewTable";

/**
 * Highlights + scrolls to the table row of the chat reference chip the user is
 * hovering. `resolveTableRowId` must return undefined while the row is not in
 * the loaded data (e.g. another page), so the scroll runs once it appears.
 */
export function useRefRowHighlight<
    TFocusedId extends string | number,
    TRowId extends string | number,
>({
    focusedId,
    resolveTableRowId,
    tableRef,
}: {
    focusedId: TFocusedId | null;
    resolveTableRowId: (focusedId: TFocusedId) => TRowId | undefined;
    tableRef?: RefObject<ViewTableHandle | null>;
}) {
    const ownRef = useRef<ViewTableHandle>(null);
    const viewTableRef = tableRef ?? ownRef;
    const selectedRowId =
        focusedId == null ? undefined : resolveTableRowId(focusedId);
    const scrolledToIdRef = useRef<TRowId | null>(null);

    useEffect(
        function scrollFocusedRefRowIntoView() {
            if (selectedRowId == null) {
                scrolledToIdRef.current = null;
                return;
            }
            if (scrolledToIdRef.current === selectedRowId) {
                return;
            }
            scrolledToIdRef.current = selectedRowId;
            viewTableRef.current?.scrollToRow(String(selectedRowId), {
                align: "center",
            });
        },
        [selectedRowId, viewTableRef],
    );

    return { viewTableRef, selectedRowId };
}

export function useLoadedRowIdResolver<TId extends string | number>(
    rows: ReadonlyArray<{ id: TId }> | null | undefined,
) {
    return useCallback(
        (id: TId) => (rows?.some((row) => row.id === id) ? id : undefined),
        [rows],
    );
}
